import { Editor } from '@tiptap/core';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import sanitizeHTML from '../../services/sanitizeHTML';
import { CUSTOM_MESSAGE_ELEMENT_ID } from './emailPreviewIframeConstants';
import {
  getEditableDisplayHtml,
  hasRealHtmlContent,
  isEditablePlaceholderContent,
} from './emailPreviewIframeUtils';

interface UseEmailPreviewEditorOptions {
  onContentChange?: (html: string) => void;
  onEnterEditMode?: () => void;
  onExitEditMode?: () => void;
  onHideSpeechBubble?: () => void;
}

function createToolbar(editor: Editor): HTMLElement {
  const toolbar = document.createElement('div');
  toolbar.className = 'email-preview-editor-toolbar';
  toolbar.setAttribute('role', 'toolbar');
  toolbar.setAttribute('aria-label', 'Email message formatting');
  toolbar.style.cssText =
    'display:flex;flex-wrap:wrap;gap:4px;padding:6px;background:#fff;border:1px solid #ccc;';

  const addButton = (label: string, run: () => void) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.title = label;
    button.style.cssText = 'padding:2px 7px;background:#fff;border:1px solid #ccc;cursor:pointer;';
    button.addEventListener('mousedown', (event) => event.preventDefault());
    button.addEventListener('click', run);
    toolbar.appendChild(button);
  };

  addButton('H1', () => editor.chain().focus().toggleHeading({ level: 1 }).run());
  addButton('H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run());
  addButton('H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run());
  addButton('B', () => editor.chain().focus().toggleBold().run());
  addButton('I', () => editor.chain().focus().toggleItalic().run());
  addButton('U', () => editor.chain().focus().toggleUnderline().run());
  addButton('• List', () => editor.chain().focus().toggleBulletList().run());
  addButton('1. List', () => editor.chain().focus().toggleOrderedList().run());
  addButton('Link', () => {
    const href = window.prompt('Enter the URL');
    if (href && /^https:\/\//i.test(href)) {
      editor.chain().focus().setLink({ href }).run();
    }
  });
  addButton('Clear', () => editor.chain().focus().unsetAllMarks().run());
  return toolbar;
}

function createEditorElements(element: HTMLElement, exitEditMode: () => void) {
  element.innerHTML = '';
  element.removeAttribute('contenteditable');
  const toolbarContainer = document.createElement('div');
  const editorContainer = document.createElement('div');
  editorContainer.className = 'email-preview-editor';
  editorContainer.style.cssText = 'min-height:80px;padding:8px;border:1px solid #ccc;';
  const statusContainer = document.createElement('div');
  statusContainer.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:4px;';
  const status = document.createElement('span');
  status.setAttribute('role', 'status');
  const doneButton = document.createElement('button');
  doneButton.type = 'button';
  doneButton.textContent = 'Done';
  doneButton.addEventListener('click', exitEditMode);
  statusContainer.append(status, doneButton);
  element.append(toolbarContainer, editorContainer, statusContainer);
  return { toolbarContainer, editorContainer, status };
}

function attachEditorEvents(
  editor: Editor,
  persist: (editor: Editor) => void,
  exitEditMode: () => void,
  getOriginalContent: () => string,
) {
  editor.view.dom.addEventListener('keydown', (event: Event) => {
    const keyEvent = event as KeyboardEvent;
    if ((keyEvent.ctrlKey || keyEvent.metaKey) && keyEvent.key === 'Enter') {
      keyEvent.preventDefault();
      persist(editor);
    } else if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault();
      exitEditMode();
    }
  });
  editor.view.dom.addEventListener('blur', (event: Event) => {
    const relatedTarget = (event as FocusEvent).relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('.email-preview-editor-toolbar, button')) return;
    if (editor.getHTML() !== getOriginalContent()) persist(editor);
  });
}

function useEditorLifecycle({
  onContentChange,
  onExitEditMode,
  onHideSpeechBubble,
}: UseEmailPreviewEditorOptions) {
  const [isInitialized, setIsInitialized] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const originalContentRef = useRef('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimers = useCallback(() => {
    for (const timer of [autoSaveTimeoutRef, statusTimeoutRef, focusTimeoutRef]) {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      clearTimers();
      editorRef.current?.destroy();
      editorRef.current = null;
    },
    [clearTimers],
  );
  const getCurrentHtml = useCallback((): string => {
    if (editorRef.current) return editorRef.current.getHTML();
    return document.getElementById(CUSTOM_MESSAGE_ELEMENT_ID)?.innerHTML || '';
  }, []);
  const exitEditMode = useCallback(() => {
    const editor = editorRef.current;
    const element = document.getElementById(CUSTOM_MESSAGE_ELEMENT_ID);
    if (!editor || !element) return;
    const html = sanitizeHTML(editor.getHTML());
    if (html !== originalContentRef.current) onContentChange?.(html);
    originalContentRef.current = html;
    clearTimers();
    editor.destroy();
    editorRef.current = null;
    element.innerHTML = getEditableDisplayHtml(html);
    element.setAttribute('contenteditable', 'true');
    element.style.cursor = 'pointer';
    setIsInitialized(false);
    onExitEditMode?.();
    onHideSpeechBubble?.();
  }, [clearTimers, onContentChange, onExitEditMode, onHideSpeechBubble]);
  return {
    isInitialized,
    setIsInitialized,
    editorRef,
    originalContentRef,
    autoSaveTimeoutRef,
    statusTimeoutRef,
    focusTimeoutRef,
    getCurrentHtml,
    exitEditMode,
  };
}

function useEditorInitialization(
  session: ReturnType<typeof useEditorLifecycle>,
  { onContentChange, onEnterEditMode }: UseEmailPreviewEditorOptions,
) {
  const {
    editorRef,
    originalContentRef,
    autoSaveTimeoutRef,
    statusTimeoutRef,
    focusTimeoutRef,
    setIsInitialized,
    exitEditMode,
  } = session;
  return useCallback(() => {
    if (editorRef.current) return;
    const element = document.getElementById(CUSTOM_MESSAGE_ELEMENT_ID);
    if (!element) return;
    const currentHtml = element.innerHTML.trim();
    const hasContent =
      hasRealHtmlContent(currentHtml) && !isEditablePlaceholderContent(currentHtml);
    const initialHtml = hasContent ? sanitizeHTML(currentHtml) : '';
    originalContentRef.current = initialHtml;
    const { toolbarContainer, editorContainer, status } = createEditorElements(
      element,
      exitEditMode,
    );
    const persist = (editor: Editor) => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      const html = sanitizeHTML(editor.getHTML());
      if (html !== originalContentRef.current) {
        originalContentRef.current = html;
        onContentChange?.(html);
      }
      status.textContent = 'updated';
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => {
        status.textContent = '';
      }, 2000);
    };
    const editor = new Editor({
      element: editorContainer,
      extensions: [
        StarterKit.configure({
          link: { openOnClick: false, isAllowedUri: (url) => /^https:\/\//i.test(url) },
        }),
        Underline,
      ],
      content: initialHtml,
      onUpdate: ({ editor: updatedEditor }) => {
        status.textContent = 'pending update';
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = setTimeout(() => persist(updatedEditor), 2000);
      },
    });
    editorRef.current = editor;
    toolbarContainer.appendChild(createToolbar(editor));
    attachEditorEvents(editor, persist, exitEditMode, () => originalContentRef.current);
    setIsInitialized(true);
    onEnterEditMode?.();
    focusTimeoutRef.current = setTimeout(() => editor.commands.focus('end'), 150);
  }, [
    autoSaveTimeoutRef,
    editorRef,
    exitEditMode,
    focusTimeoutRef,
    onContentChange,
    onEnterEditMode,
    originalContentRef,
    setIsInitialized,
    statusTimeoutRef,
  ]);
}

export function useEmailPreviewEditor(options: UseEmailPreviewEditorOptions) {
  const session = useEditorLifecycle(options);
  const initializeEditor = useEditorInitialization(session, options);
  const { editorRef } = session;
  const { onHideSpeechBubble } = options;
  const handleClick = useCallback(
    (event: ReactMouseEvent | MouseEvent) => {
      if (editorRef.current) return;
      const target = event.target as HTMLElement;
      if (target.closest('button, .email-preview-editor-toolbar, .email-preview-editor')) return;
      event.preventDefault();
      event.stopPropagation();
      onHideSpeechBubble?.();
      initializeEditor();
    },
    [editorRef, initializeEditor, onHideSpeechBubble],
  );
  return {
    getCurrentHtml: session.getCurrentHtml,
    handleClick,
    isInitialized: session.isInitialized,
  };
}
